import { useAppDispatch, useAppSelector } from '@/store';
import classNames from './TheSidebar.module.scss';
import { authSelector } from '@/store/reducers/auth';
import userThunks from '@/store/thunks/user';
import { View } from '@/types/common';
import SectionSeperator from '@/components/SectionSeperator/SectionSeperator';
import logo from '@/assets/svg/logo_new.svg';
import UserInfo from '@/components/UserInfo/UserInfo';
import ViewToggler from '@/components/ViewToggler/ViewToggler';
import AchievementsSection from '../AchievementsSection/AchievementsSection';
import UseCurrentRoute from '@/hooks/UseCurrentRoute';
import { ROUTE_NAMES } from '@/router';
import ImproveActivity from '@/components/ImproveActivity/ImproveActivity';
import UseLevel from '@/hooks/UseLevel';
import { useEffect, useState } from 'react';
import UserStreak from '../UserStreak/UserStreak';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  viewTabs: View[];
  emitSelectView: (view: View) => void;
}

const UserSidebar = ({ viewTabs, emitSelectView }: SidebarProps) => {
  const [isDataLoading, setIsDataLoading] = useState(true);
  const { user } = useAppSelector(authSelector);
  const { achievementOfTheWeek } = useAppSelector(authSelector);
  const dispatch = useAppDispatch();
  const currentRoute = UseCurrentRoute();
  const { improvableRecentActivities } = useAppSelector(authSelector);
  const { getLevelText } = UseLevel();
  const { t } = useTranslation();

  const levelText = getLevelText(user?.progress?.userLevel);

  useEffect(() => {
    let isActive: boolean = true;

    setIsDataLoading(true);

    const initData = async () => {
      try {
        if (user?.progress?.userLevel) {
          await Promise.allSettled([
            dispatch(userThunks.fetchImprovableRecentActivities()).unwrap(),
            dispatch(userThunks.fetchAchievementOfTheWeek()).unwrap(),
          ]);
        }
      } catch (ex) {}

      if (isActive) {
        setIsDataLoading(false);
      }
    };

    initData();

    return () => {
      isActive = false;
    };
  }, [user]);

  return (
    <>
      <img src={logo} alt='Hello Britannica' />
      {user && (
        <UserInfo
          fullname={`${user.metadata.firstName} ${user.metadata.lastName}`}
          firstName={user.metadata.firstName}
          lastName={user.metadata.lastName}
          profileImage={user?.metadata?.userSettings?.avatar?.avatarName}
          backgroundColor={user?.metadata?.userSettings?.avatar?.avatarColor}
          level={levelText}
          grade={user?.metadata?.organizationDetails?.grade}
          coins={user?.progress?.coins}
        />
      )}
      <ViewToggler className='mt-10' viewTabs={viewTabs} emitSelectView={emitSelectView} />

      {!isDataLoading && (
        <>
          {user && achievementOfTheWeek !== null && achievementOfTheWeek.data && (
            <AchievementsSection
              firstname={user?.metadata.firstName}
              achievement={achievementOfTheWeek}
            />
          )}
          {improvableRecentActivities && improvableRecentActivities.length > 0 && (
            <>
              <SectionSeperator className='my-10' />
              <ImproveActivity activities={improvableRecentActivities} />
            </>
          )}
        </>
      )}

      <SectionSeperator className='my-6' />
      <>
        <span className={classNames.title}>{t('userStreak.title')}</span>
        <span className={classNames.subTitle}>{t('userStreak.subTitle')}</span>
        <div className={classNames.streakContainer}>
          <UserStreak
            showTitle={false}
            streak={user?.progress.userStreakResponses ?? []}
            className={classNames.userStreak}
          />
        </div>
      </>
    </>
  );
};

export default UserSidebar;
